import { DEVICE_LABELS, type CompetitionDevices, type DeviceField, type DeviceRow } from '@/api/competitionDetail'
import { EmptyState, formatInteger, formatPercent } from '@/design'
import type { GlobalFilters } from '@/lib/filters'

import { TabRead } from '../pieces'
import { useCompetitionDevices } from '../queries'
import { describeDetailReason, Reasoned, UNDEFINED_FIGURE } from '../reasons'

const CONSENT_CAPTION =
  'Consented sessions only: these are measured browser sessions, kept only for visitors who allowed analytics. A visitor who declined is in no row, though their purchase is still in the summary’s orders.'

/**
 * The competition's measured sessions by device (`…/devices`), read when the tab opens:
 * those that viewed it, bought it, and viewed then bought it, and that conversion rate.
 * Crawlers are left out of every row and counted apart.
 */
export function DevicesTab({ competitionId, filters }: { competitionId: string; filters: GlobalFilters }) {
  const read = useCompetitionDevices(competitionId, filters)
  return (
    <TabRead read={read} loading="Loading devices">
      {(devices) => <DevicesBody devices={devices} />}
    </TabRead>
  )
}

function DevicesBody({ devices }: { devices: CompetitionDevices }) {
  const { notAvailable } = devices
  const tracking = notAvailable.viewingSessions
  if (tracking && devices.devices.every((row) => row.viewingSessions === null)) {
    return (
      <>
        <EmptyState variant="not-live" compact description={describeDetailReason(tracking).why} />
        <p className="cd-note">{CONSENT_CAPTION}</p>
      </>
    )
  }
  const cell = (row: DeviceRow, field: Exclude<DeviceField, 'botSessions'>, show: (value: number) => string) => {
    const value = row[field]
    if (value !== null) return show(value)
    const reason = notAvailable[field]
    return reason ? <Reasoned reason={reason} /> : UNDEFINED_FIGURE
  }
  const caption = 'Sessions by device'
  return (
    <div className="cd-stack">
      <p className="cd-note">{CONSENT_CAPTION}</p>
      <div className="ci-table-wrap" role="region" aria-label={caption} tabIndex={0}>
        <table className="ci-table">
          <caption className="ci-visually-hidden">{caption}</caption>
          <thead>
            <tr>
              <th scope="col">Device</th>
              <th scope="col" data-align="end">
                Viewing sessions
              </th>
              <th scope="col" data-align="end" className="cd-optional">
                Purchasing sessions
              </th>
              <th scope="col" data-align="end">
                Viewed, then bought
              </th>
              <th scope="col" data-align="end">
                Conversion
              </th>
            </tr>
          </thead>
          <tbody>
            {devices.devices.map((row) => (
              <tr key={row.device} data-device={row.device}>
                <th scope="row">{DEVICE_LABELS[row.device] ?? row.device}</th>
                <td data-align="end">{cell(row, 'viewingSessions', formatInteger)}</td>
                <td data-align="end" className="cd-optional">
                  {cell(row, 'purchasingSessions', formatInteger)}
                </td>
                <td data-align="end">{cell(row, 'convertedSessions', formatInteger)}</td>
                <td data-align="end">{cell(row, 'conversionRate', (value) => formatPercent(value))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="cd-note">
        Crawler sessions, left out of every row:{' '}
        {devices.botSessions === null ? (
          notAvailable.botSessions ? (
            <Reasoned reason={notAvailable.botSessions} />
          ) : (
            UNDEFINED_FIGURE
          )
        ) : (
          formatInteger(devices.botSessions)
        )}
        . “Unknown” is a session none of whose events for this competition said which device it was.
      </p>
    </div>
  )
}
